import {
    useEffect,
    useMemo,
    type FC
} from "react";
import {
    type ViewStyle,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IChipProps from "./type";
import stylesheet, {
    getChipIsStretched,
    getChipColors,
    getChipSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    getCrossfadeStyle,
    usePressScale,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    Check as CheckIcon,
    X as XIcon
} from "lucide-react-native";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";
import AnimatedIcon from "../animatedIcon";
import StateLayer from "../stateLayer";
import Loading from "../loading";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const Chip: FC<IChipProps> = ({
    displayBehaviourWhileLoading,
    spreadBehaviour = "baseline",
    isClosable: isClosableProp,
    isPressScaleEnabled = true,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    accessibilityRole,
    isLoading = false,
    variant = "tonal",
    customTitleColor,
    type = "neutral",
    customIconColor,
    size = "medium",
    customLocalize,
    icon: IconProp,
    customColor,
    customTheme,
    onLongPress,
    accessible,
    isSelected,
    onHoverOut,
    onPressOut,
    titleStyle,
    onHoverIn,
    onPressIn,
    onClose,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    role,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const {
        interactionHandlers: closeInteractionHandlers,
        isFocusVisible: isCloseFocusVisible,
        isHovered: isCloseHovered,
        isPressed: isClosePressed
    } = useInteractionState({
        isDisabled
    });

    const isInteractive = !!onPress || !!onLongPress;

    const isClosable = isClosableProp ?? !!onClose;

    const isCloseButton = isClosable && !!onClose;

    const pressScaleStyle = usePressScale({
        isEnabled: isPressScaleEnabled && isInteractive,
        isDisabled: isDisabled || isLoading,
        customTheme,
        isPressed
    });

    const {
        isRendered: isCheckRendered,
        progress: checkProgress,
        isReduceMotionEnabled
    } = usePresence({
        isNativeDriverEnabled: !!IconProp,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        isVisible: !!isSelected,
        customTheme
    });

    const checkCrossfadeStyle = useMemo(() => getCrossfadeStyle({
        progress: checkProgress,
        isReduceMotionEnabled
    }), [
        isReduceMotionEnabled,
        checkProgress
    ]);

    const {
        isRendered: isLoadingRendered,
        progress: loadingProgress
    } = usePresence({
        isNativeDriverEnabled: false,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        isVisible: isLoading,
        customTheme
    });

    const isIconOnly = !!IconProp && !title && !isClosable;

    const isToggle = isInteractive && isSelected !== undefined;

    const isNativeToggle = isToggle && Platform.OS !== "web";

    const webToggleProps = isToggle && Platform.OS === "web" ? {
        "aria-pressed": isSelected
    } : {};

    const currentSize = getChipSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const checkSlotDynamicStyle = useMemo(() => {
        if(isReduceMotionEnabled) {
            return undefined;
        }

        return {
            marginEnd: checkProgress.interpolate({
                outputRange: [
                    -spaces.spacingXs,
                    0
                ],
                inputRange: [
                    0,
                    1
                ]
            }),
            width: checkProgress.interpolate({
                outputRange: [
                    0,
                    currentSize.iconSize
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        };
    }, [
        currentSize.iconSize,
        isReduceMotionEnabled,
        spaces.spacingXs,
        checkProgress
    ]);

    const loadingSlotDynamicStyle = useMemo(() => {
        if(isReduceMotionEnabled) {
            return undefined;
        }

        return {
            marginEnd: loadingProgress.interpolate({
                outputRange: [
                    -spaces.spacingXs,
                    0
                ],
                inputRange: [
                    0,
                    1
                ]
            }),
            width: loadingProgress.interpolate({
                outputRange: [
                    0,
                    currentSize.iconSize
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        };
    }, [
        currentSize.iconSize,
        isReduceMotionEnabled,
        spaces.spacingXs,
        loadingProgress
    ]);

    const currentColors = getChipColors({
        isSelected: !!isSelected,
        customTitleColor,
        customIconColor,
        customColor,
        isDisabled,
        variant,
        colors,
        type
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const isStretched = getChipIsStretched({
        surfaceStyle
    });

    const {
        closePlaceholder: closePlaceholderDynamicStyle,
        closeIndicator: closeIndicatorDynamicStyle,
        closeIcon: closeIconDynamicStyle,
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        anchor: anchorDynamicStyle,
        close: closeDynamicStyle,
        root: rootDynamicStyle
    } = useStyles({
        isCloseFocusVisible,
        userStyle: style,
        spreadBehaviour,
        isFocusVisible,
        currentColors,
        isCloseButton,
        isInteractive,
        shapeConfigs,
        touchTargets,
        currentSize,
        isStretched,
        pointerType,
        isDisabled,
        isIconOnly,
        isLoading,
        radiuses,
        borders,
        colors,
        spaces,
        type
    });

    useEffect(() => {
        if(displayBehaviourWhileLoading === undefined) {
            return;
        }

        warnOnce({
            message: "Chip: \"displayBehaviourWhileLoading\" is deprecated and will be removed in the next major version. A loading chip keeps its type colours and always blocks presses."
        });
    }, [
        displayBehaviourWhileLoading
    ]);

    useEffect(() => {
        if(!isClosable || onClose) {
            return;
        }

        warnOnce({
            message: "Chip: \"isClosable\" is true but \"onClose\" is not set, so the close icon is decorative. Pass \"onClose\" to render a separate remove button, or remove \"isClosable\" to hide the icon."
        });
    }, [
        isClosable,
        onClose
    ]);

    useEffect(() => {
        if(title || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Chip: a chip without a title needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        ariaLabel,
        title
    ]);

    const renderStateLayer = () => {
        if(!isInteractive) {
            return null;
        }

        return <StateLayer
            overlayColor={currentColors.overlayColor}
            isHovered={!isLoading && isHovered}
            isPressed={!isLoading && isPressed}
            borderRadius={radiuses.chip}
            borderWidth={borders.line}
            customTheme={customTheme}
            isSquircle={true}
        />;
    };

    const renderCheck = () => {
        if(!IconProp) {
            return <Animated.View
                style={[
                    stylesheet.checkSlot,
                    checkSlotDynamicStyle
                ]}
            >
                <Animated.View
                    style={checkCrossfadeStyle.incoming}
                >
                    <CheckIcon
                        color={currentColors.iconColor}
                        size={currentSize.iconSize}
                    />
                </Animated.View>
            </Animated.View>;
        }

        return <View>
            <Animated.View
                style={checkCrossfadeStyle.outgoing}
            >
                <IconProp
                    customColor={currentColors.iconColor}
                    color={currentColors.iconColorKey}
                    size={currentSize.iconSize}
                />
            </Animated.View>
            <Animated.View
                style={[
                    stylesheet.checkContainer,
                    checkCrossfadeStyle.incoming
                ]}
            >
                <CheckIcon
                    color={currentColors.iconColor}
                    size={currentSize.iconSize}
                />
            </Animated.View>
        </View>;
    };

    const renderLoading = () => {
        return <Loading
            customColor={currentColors.titleColor}
            size={currentSize.iconSize}
            customTheme={customTheme}
            aria-hidden={true}
        />;
    };

    const renderLeading = () => {
        if(isLoading && IconProp) {
            return renderLoading();
        }

        if(isLoadingRendered && !IconProp) {
            return <Animated.View
                style={[
                    stylesheet.checkSlot,
                    loadingSlotDynamicStyle
                ]}
            >
                <Animated.View
                    style={{
                        opacity: loadingProgress
                    }}
                >
                    {renderLoading()}
                </Animated.View>
            </Animated.View>;
        }

        if(isCheckRendered) {
            return renderCheck();
        }

        if(!IconProp) {
            return null;
        }

        return <IconProp
            customColor={currentColors.iconColor}
            color={currentColors.iconColorKey}
            size={currentSize.iconSize}
        />;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        return <Text
            customColor={currentColors.titleColor}
            variant={currentSize.labelVariant}
            customTheme={customTheme}
            style={[
                titleStyle,
                stylesheet.title
            ]}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const renderCloseLayer = () => {
        if(!isCloseButton) {
            return null;
        }

        return <StateLayer
            overlayColor={currentColors.overlayColor}
            isHovered={!isLoading && isCloseHovered}
            isPressed={!isLoading && isClosePressed}
            borderRadius={radiuses.full}
            customTheme={customTheme}
        />;
    };

    const renderCloseIndicator = () => {
        if(!isClosable) {
            return null;
        }

        return <View
            style={[
                stylesheet.closeIndicator,
                closeIndicatorDynamicStyle
            ]}
            aria-hidden={true}
        >
            {renderCloseLayer()}
            <View
                style={closePlaceholderDynamicStyle}
            >
                {
                    isCloseButton ?
                        null
                    :
                        <XIcon
                            color={currentColors.titleColor}
                            size={currentSize.iconSize}
                        />
                }
            </View>
        </View>;
    };

    const renderSurface = () => {
        return <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {renderStateLayer()}
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderLeading()}
                {renderTitle()}
                {renderCloseIndicator()}
            </View>
        </View>;
    };

    const renderMain = (mainStyle?: ViewStyle) => {
        if(!isInteractive) {
            return <View
                {...props}
                role={role ?? (title ? undefined : "img")}
                accessibilityLabel={accessibilityLabel}
                accessible={title ? accessible : true}
                aria-disabled={isDisabled}
                style={[
                    mainStyle,
                    stylesheet.container,
                    containerDynamicStyle
                ]}
                aria-label={ariaLabel}
                aria-busy={isLoading}
            >
                {renderSurface()}
            </View>;
        }

        return <AnimatedPressable
            {...props}
            {...interactionHandlers}
            {...webToggleProps}
            accessibilityRole={isNativeToggle ? "togglebutton" : accessibilityRole}
            aria-checked={isNativeToggle ? isSelected : undefined}
            onLongPress={isLoading ? undefined : onLongPress}
            role={isNativeToggle ? role : role ?? "button"}
            onPress={isLoading ? undefined : onPress}
            accessibilityLabel={accessibilityLabel}
            style={[
                mainStyle,
                stylesheet.container,
                containerDynamicStyle,
                !isCloseButton && pressScaleStyle
            ]}
            aria-disabled={isDisabled}
            accessible={accessible}
            aria-label={ariaLabel}
            aria-busy={isLoading}
            disabled={isDisabled}
        >
            {renderSurface()}
        </AnimatedPressable>;
    };

    const renderCloseButton = () => {
        return <Pressable
            {...closeInteractionHandlers}
            accessibilityLabel={title ? `${localize("remove")}: ${title}` : localize("remove")}
            onPress={isLoading ? undefined : onClose}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            style={[
                stylesheet.close,
                closeDynamicStyle
            ]}
            role="button"
        >
            <AnimatedIcon
                customColor={currentColors.titleColor}
                style={closeIconDynamicStyle}
                size={currentSize.iconSize}
                animationTrigger="hover"
                animation="signature"
                icon={XIcon}
            />
        </Pressable>;
    };

    if(!isCloseButton) {
        return renderMain(containerStyle);
    }

    return <View
        style={[
            containerStyle,
            stylesheet.root,
            rootDynamicStyle
        ]}
    >
        <Animated.View
            style={[
                stylesheet.anchor,
                anchorDynamicStyle,
                pressScaleStyle
            ]}
        >
            {renderMain()}
            {renderCloseButton()}
        </Animated.View>
    </View>;
};
export default Chip;
